import { getCardShortName } from '../../utils/cards';
import type { PlayTreeNode } from './DiffereStatsPlayTreeDrill';

/** Libellé « 3♠ ou 4♠ » à partir des cartes humaines observées sur la branche. */
export function formatPlayTreeHumanCardsLabel(cards: number[]): string {
  if (cards.length === 0) return '—';
  if (cards.length === 1) return getCardShortName(cards[0]! as never);
  return cards.map(c => getCardShortName(c as never)).join(' ou ');
}

/** Libellé de séquence à partir des cartes réellement jouées sur un chemin complet. */
export function formatPlayTreePathLabelsFromHumanCards(humanCardsByTrick: number[][]): string {
  return humanCardsByTrick.map(cards => formatPlayTreeHumanCardsLabel(cards)).join(' → ');
}

/** Agrège les cartes humaines par pli à partir des joueurs correspondant au chemin. */
export function humanCardsByTrickFromPlayerMatches(
  matches: Array<{ humanCards: number[] }>,
  depth: number,
): number[][] {
  const byTrick: Set<number>[] = Array.from({ length: depth }, () => new Set());
  for (const m of matches) {
    for (let t = 0; t < depth && t < m.humanCards.length; t++) {
      const card = m.humanCards[t];
      if (card != null) byTrick[t]!.add(card);
    }
  }
  return byTrick.map(s => [...s].sort((a, b) => a - b));
}

export function playTreePathKey(path: string[]): string {
  return path.join('|');
}

export interface PlayTreePathLabelInfo {
  humanCardsByTrick: number[][];
  humanLedByTrick?: Array<boolean | undefined>;
}

export function buildPlayTreePathLabelMap(
  summaries: Array<{
    path: string[];
    humanCardsByTrick: number[][];
    humanLedByTrick?: Array<boolean | undefined>;
  }>,
): Map<string, PlayTreePathLabelInfo> {
  const map = new Map<string, PlayTreePathLabelInfo>();
  for (const s of summaries) {
    map.set(playTreePathKey(s.path), {
      humanCardsByTrick: s.humanCardsByTrick,
      humanLedByTrick: s.humanLedByTrick,
    });
  }
  return map;
}

/**
 * Libellé lisible pour une clé de branche (card:42, eq:12, eq:12#c0…).
 * Préférer {@link formatPlayTreeNodeLabel} quand humanCards est disponible sur le nœud.
 */
export function formatPlayTreeKeyLabel(key: string): string {
  const strictSub = key.match(/^(eq:\d+)#c(\d+)$/);
  if (strictSub) {
    const idx = Number(strictSub[1]!.slice(3));
    const subN = Number(strictSub[2]!) + 1;
    if (Number.isFinite(idx)) {
      return `${getCardShortName((idx + 1) as never)} (classe · sous-groupe ${subN})`;
    }
  }

  const base = key.split('#')[0] ?? key;
  const genericStrict = key.includes('#') ? ' (strict)' : '';

  if (base.startsWith('eq:')) {
    const idx = Number(base.slice(3));
    if (Number.isFinite(idx)) {
      return getCardShortName((idx + 1) as never) + ' (classe)' + genericStrict;
    }
  }
  if (key.startsWith('card:')) {
    const id = Number(key.slice(5));
    if (Number.isFinite(id)) return getCardShortName(id as never);
  }
  return key;
}

export function formatPlayTreeNodeLabel(node: Pick<PlayTreeNode, 'key' | 'humanCards'>): string {
  if (node.humanCards != null && node.humanCards.length > 0) {
    const strictSub = node.key.match(/^eq:\d+#c(\d+)$/);
    const base = formatPlayTreeHumanCardsLabel(node.humanCards);
    if (strictSub) {
      const subN = Number(strictSub[1]!) + 1;
      return `${base} · sous-groupe ${subN}`;
    }
    return base;
  }
  return formatPlayTreeKeyLabel(node.key);
}

export function resolvePlayTreePathNodes(root: PlayTreeNode, path: string[]): PlayTreeNode[] {
  const nodes: PlayTreeNode[] = [];
  let level = root.children;
  for (const key of path) {
    const node = level.find(c => c.key === key);
    if (!node) break;
    nodes.push(node);
    level = node.children;
  }
  return nodes;
}

export function formatPlayTreePathLabelsFromRoot(root: PlayTreeNode, path: string[]): string {
  return resolvePlayTreePathNodes(root, path).map(formatPlayTreeNodeLabel).join(' → ');
}

export function formatPlayTreePathLabels(path: string[]): string {
  return path.map(formatPlayTreeKeyLabel).join(' → ');
}
